import jwtDecode from 'jwt-decode';
import { type Selector, createSelector } from 'reselect';

import { AuthType, UserRoleType } from '../models';

import type { AuthSlice, AuthState, DecodedCloudToken, DecodedSsoToken } from './auth.types';

const slice = (state: AuthState): AuthSlice => state.auth;

export const selectToken = (state: AuthState) => slice(state).token;
export const selectSsoToken = (state: AuthState) => slice(state).ssoToken;
export const selectAuthType = (state: AuthState) => slice(state).tenantAuth.type;
export const selectAuthLoading = (state: AuthState) => slice(state).tenantAuth.loading;

export const makeSelectDecoddedToken = <Token extends Record<string, unknown>>(
    rawTokenSelector: Selector<AuthState, string | undefined>,
) =>
    createSelector(rawTokenSelector, token => {
        if (typeof token !== 'string') {
            return null;
        }

        try {
            return jwtDecode<Token>(token);
        } catch (e) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Failed to decode JWT', e);
            }

            return null;
        }
    });

export const selectDecodedToken = makeSelectDecoddedToken<DecodedCloudToken>(selectToken);
export const selectDecodedSsoToken = makeSelectDecoddedToken<DecodedSsoToken>(selectSsoToken);

export const isTokenJtiValid = (
    ssoToken: DecodedSsoToken | null,
    cvpnToken: DecodedCloudToken | null,
) => !!ssoToken && !!cvpnToken && ssoToken.jti === cvpnToken.master_jti;

/**
 * @returns true if product/sso tokens are preset,
 * sso.jti and product.masterJti matches
 * and product token is not expired.
 */
export const selectIsLoggedIn = createSelector(
    selectDecodedSsoToken,
    selectDecodedToken,
    (ssoToken, cvpnToken) => {
        if (!ssoToken || !cvpnToken || !isTokenJtiValid(ssoToken, cvpnToken)) {
            return false;
        }

        const now = Date.now().valueOf() / 1000;
        const expired = now > cvpnToken.exp;
        return !expired;
    },
);

//
// Role selectors
//

/**
 * @returns `sso_token.authorities` string array or empty array if fails.
 */
export const selectUserRoles = createSelector(selectDecodedSsoToken, jwt => jwt?.authorities ?? []);

export const selectIsMember = createSelector(
    selectUserRoles,
    roles => roles.includes(UserRoleType.Member) && !roles.includes(UserRoleType.Admin),
);

export const selectIsAdmin = createSelector(selectUserRoles, roles =>
    roles.includes(UserRoleType.Admin),
);

/**
 * @returns true for enrolled owner user role.
 */
export const selectIsOwner = createSelector(selectUserRoles, roles =>
    roles.includes(UserRoleType.Owner),
);

/**
 * @returns true if user is enrolled owner or admin.
 */
export const selectIsOwnerOrAdmin = createSelector(
    selectIsAdmin,
    selectIsOwner,
    (isAdmin, isOwner) => isAdmin || isOwner,
);

/**
 * @returns true if owner is not enrolled.
 */
export const selectIsPendingOwner = createSelector(
    selectUserRoles,
    roles => roles.includes(UserRoleType.PendingOwner) && !roles.includes(UserRoleType.Owner),
);

/**
 * @returns true for both Enrolled and non-Enrolled owner role.
 */
export const selectIsOwnerAccount = createSelector(
    selectUserRoles,
    roles => roles.includes(UserRoleType.PendingOwner) || roles.includes(UserRoleType.Owner),
);

/**
 * @returns user-friendly string representation of current role that can be displayed on UI.
 */
export const selectFrieldlyRole = createSelector(selectUserRoles, roles => {
    if (!Array.isArray(roles)) {
        return '';
    }

    const isEnrolledOwner = roles.includes(UserRoleType.Owner);
    const isPendingOwner = roles.includes(UserRoleType.PendingOwner);
    const isAdmin = roles.includes(UserRoleType.Admin);

    let role = 'Member';

    if (isAdmin) {
        role = 'Admin';
    } else if (isEnrolledOwner || isPendingOwner) {
        role = 'Owner';
    }

    return role;
});

//
// User info selectors
//

/**
 * @returns `sso_token.user_name` string.
 */
export const selectUserId = createSelector(selectDecodedSsoToken, jwt => jwt?.user_name ?? '');

/**
 * @returns `sso_token.idp_user_id` string.
 */
export const selectIdpUserId = createSelector(selectDecodedSsoToken, jwt => jwt?.idp_user_id ?? '');

/**
 * @returns friendly user name to be displayed on UI.
 */
export const selectUserName = createSelector(
    selectDecodedSsoToken,
    selectIdpUserId,
    selectUserId,
    selectUserRoles,
    (jwt, idpUserId, userId, roles) => {
        if (idpUserId) {
            return idpUserId;
        }
        const isNotOwner =
            roles.includes(UserRoleType.Member) || roles.includes(UserRoleType.Admin);
        const trimEmailPart = (t: string) => t.split('@')[0]!;
        if (
            isNotOwner &&
            !!jwt?.idp_user_id &&
            (jwt.auth_type === AuthType.Saml || jwt.auth_type === AuthType.Ldap)
        ) {
            return trimEmailPart(jwt.idp_user_id);
        }
        return isNotOwner ? trimEmailPart(userId) : userId;
    },
);

/**
 * @returns true if current auth type is SAML
 */
export const selectIsSamlUser = createSelector(
    selectDecodedSsoToken,
    jwt => jwt?.auth_type === AuthType.Saml,
);

/**
 * @returns true if current auth type is LDAP
 */
export const selectIsLdapUser = createSelector(
    selectDecodedSsoToken,
    jwt => jwt?.auth_type === AuthType.Ldap,
);

/**
 * Mainly used in GTM events.
 *
 * @returns `cvpn_token.credentials_id` string.
 */
export const selectCredentialsId = createSelector(
    selectDecodedToken,
    jwt => jwt?.credentials_id ?? '',
);

/**
 * This is your Tenant name.
 *
 * @returns `sso_token.client_domain` string.
 */
export const selectClientDomain = createSelector(
    selectDecodedSsoToken,
    jwt => jwt?.client_domain ?? '',
);

/**
 * @returns `sso_token.jti` string.
 */
export const selectSsoJTI = createSelector(selectDecodedSsoToken, jwt => jwt?.jti ?? '');

/**
 * @returns `sso_token.perc` parsed to number or `0` if fails.
 */
export const selectSsoPercentile = createSelector(selectDecodedSsoToken, jwt =>
    Number(jwt?.perc ?? '0'),
);

/**
 * @returns true for users with corresponding `sso_token.awsmp` flag.
 */
export const selectIsAWSCustomer = createSelector(
    selectDecodedSsoToken,
    jwt => jwt?.awsmp === 'true',
);

/**
 * @returns `sso_token.tenants` string array or empty array if fails.
 */
export const selectMultiTenants = createSelector(
    selectDecodedSsoToken,
    ssoToken => ssoToken?.tenants?.split(',') ?? [],
);
